import { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { useFetchGet } from "hooks/fetch/useFetchGet";
import { MachineStatusCreateRow } from "components/CaseManagement/CaseDetail/MachineStatus/MachineStatusCreateRow";
import { MachineStatusEditRow } from "components/CaseManagement/CaseDetail/MachineStatus/MachineStatusEditRow";
import { MachineStatusStaticRow } from "components/CaseManagement/CaseDetail/MachineStatus/MachineStatusStaticRow";
import { CustomPrompt } from "components/CustomPrompt";
import { useHistory } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon, AddCircle } from "@mui/icons-material";
import { caseIdToDisplayId } from "core/utils/str";
import { dateFormat } from "core/datetime";
import buildUserName from "core/utils/build_user_name";
import { useLoadCounter } from "hooks/useLoadCounter";

export default function MachineStatusAccordion({
  caseId,
  machineId,
  defaultAccordionExpanded
}) {
  const [editMode, setEditMode] = useState(); // "post" || "patch" || null
  const history = useHistory();

  // When Case ID clicked, open case detail page with a new tab
  const handleOpenInNewTab = useCallback(
    caseId => {
      const handle = window.open(
        history.createHref({
          pathname: `${caseId}`
        }),
        "_blank"
      );
      handle.blur();
      window.focus();
    },
    [history]
  );

  // Machine status types API
  const {
    loading: machineStatusTypesLoading,
    response: machineStatusTypesResponse
  } = useFetchGet("machine_status_types");

  // Machine status API
  const {
    loading: machineStatusLoading,
    response: machineStatusResponse,
    refetch: machineStatusRefetch
  } = useFetchGet(
    `machine_status?machine_id__in=${machineId}&ordering=-date_actioned`
  );

  const tableLoading = machineStatusLoading || machineStatusTypesLoading;
  const tableLoadCount = useLoadCounter(tableLoading);

  // Reformat the machine status data to display for user
  const formattedStatusList = useMemo(() => {
    const grouppedList = [];
    let currentGroup;
    let dateFormatOptions = {
      format: "Do MMM YYYY h:mma"
    };

    for (const s of machineStatusResponse?.results || []) {
      currentGroup = [];
      let displayId;
      let displayDateActioned;
      let displayStatus;

      // displayId (Machine statuses with no case associated are set to "-")
      if (s.case) displayId = caseIdToDisplayId(s.case);
      else displayId = "-";

      // displayDateActioned
      displayDateActioned = s.date_actioned
        ? dateFormat(s.date_actioned, dateFormatOptions)
        : "Not set";

      // displayStatus
      const statusType = machineStatusTypesResponse?.results.find(t => {
        return t.id === s.status;
      });
      displayStatus = statusType?.name || "Unknown";

      for (const rts of s.rts_changes) {
        let displayRts;
        if (rts.expected_rts) {
          displayRts = dateFormat(rts.expected_rts, dateFormatOptions);
        } else if (s.status === 1) {
          // Online status
          displayRts = "N/A";
        } else {
          displayRts = "Not set";
        }

        const obj = {
          id: s.id,
          machine: s.machine,
          case: s.case,
          status: s.status,
          date_actioned: s.date_actioned,
          expected_rts: rts.expected_rts,
          change_date: dateFormat(rts.change_date, dateFormatOptions),
          change_user: rts.change_user,
          change_user_full_name: buildUserName({
            first_name: rts.change_user_first_name,
            last_name: rts.change_user_last_name
          }),
          displayId: displayId,
          displayDateActioned: displayDateActioned,
          displayStatus: displayStatus,
          displayRts: displayRts
        };
        currentGroup.push(obj);
      }
      grouppedList.push(currentGroup);
    }

    return grouppedList;
  }, [machineStatusResponse?.results, machineStatusTypesResponse?.results]);

  // Destructure current machine status (editable) and remaining machine statuses (non-editable)
  const [currentMachineStatus, staticMachineStatuses] = useMemo(() => {
    if (editMode === "patch" || !Boolean(editMode)) {
      if (formattedStatusList.length > 0) {
        const other = formattedStatusList.slice(1).map(element => {
          return element[0];
        });

        return [formattedStatusList[0][0], other];
      } else {
        return [undefined, []];
      }
    }

    //create mode
    if (formattedStatusList.length > 0) {
      const other = formattedStatusList.slice(1).map(element => {
        return element[0];
      });
      return [undefined, [formattedStatusList[0][0], ...other]];
    } else {
      return [undefined, []];
    }
  }, [formattedStatusList, editMode]);

  const handleSuccessSave = useCallback(() => {
    machineStatusRefetch();
    setEditMode();
  }, [machineStatusRefetch]);

  return (
    <>
      <CustomPrompt
        shouldBlockNavigation={Boolean(editMode)}
        content={
          "You have unsaved changes in the machine status log. Are you sure you want to navigate away?"
        }
      />
      <Accordion defaultExpanded={defaultAccordionExpanded} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography style={{ marginBottom: 0 }} variant="h3">
            Machine Status Log
          </Typography>
        </AccordionSummary>
        <CustomAccordionDetails>
          <ButtonWrapper>
            <Button
              variant="outlined"
              color="primary"
              aria-label="Change status button"
              startIcon={<AddCircle />}
              disabled={Boolean(editMode) || tableLoading}
              onClick={() => setEditMode("post")}
            >
              Change status
            </Button>
          </ButtonWrapper>

          <TableScrollWrapper>
            <MachineTable
              data-qa="MachineStatus-Table-Statuses"
              stickyHeader
              aria-label="Machine status table"
            >
              <TableHead>
                <TableRow>
                  <TableClearCell size="small">Status</TableClearCell>
                  <TableClearCell size="small">Date Set</TableClearCell>
                  <TableClearCell size="small">Expected RTS</TableClearCell>
                  <TableClearCell size="small">Case ID</TableClearCell>
                  <TableClearCell size="small"></TableClearCell>
                </TableRow>
              </TableHead>
              <TableBody
                data-qa_loading={tableLoading}
                data-qa_load_count={tableLoadCount}
              >
                {/* Table loading indicator */}
                {tableLoading && (
                  <TableRow>
                    <TableLoadingCell colSpan={5}>
                      <LinearProgress />
                    </TableLoadingCell>
                  </TableRow>
                )}

                {/* Non Edit mode */}
                {formattedStatusList.length > 0 && !Boolean(editMode) && (
                  <MachineStatusStaticRow
                    loading={tableLoading}
                    caseId={caseId}
                    isEditable={true}
                    setEditMode={setEditMode}
                    machineStatus={currentMachineStatus}
                    onHandleOpenInNewTab={handleOpenInNewTab}
                    machineStatusRefetch={machineStatusRefetch}
                    history={formattedStatusList[0]}
                  />
                )}

                {/* Post(create) mode */}
                {editMode === "post" && (
                  <MachineStatusCreateRow
                    loading={tableLoading}
                    caseId={caseId}
                    machineId={machineId}
                    machineStatusList={machineStatusResponse?.results}
                    machineStatusTypes={machineStatusTypesResponse?.results}
                    onSuccess={handleSuccessSave}
                    onCancel={() => setEditMode()}
                  />
                )}

                {/* Patch(edit) mode */}
                {editMode === "patch" && (
                  <MachineStatusEditRow
                    loading={tableLoading}
                    machineStatus={currentMachineStatus}
                    machineStatusTypes={machineStatusTypesResponse?.results}
                    onSuccess={handleSuccessSave}
                    onCancel={() => setEditMode()}
                  />
                )}

                {/* Static rows or placeholder row */}
                {formattedStatusList.length > 0 ? (
                  staticMachineStatuses?.map((machineStatus, index) => {
                    const i = editMode === "post" ? index : index + 1;
                    return (
                      <MachineStatusStaticRow
                        key={`machine_status_row_${index}`}
                        caseId={caseId}
                        machineStatus={machineStatus}
                        onHandleOpenInNewTab={handleOpenInNewTab}
                        history={formattedStatusList[i]?.slice(0) || []}
                      />
                    );
                  })
                ) : (
                  <TableRow>
                    <PlaceholderCell
                      colSpan={5}
                      aria-label="Machine status table placeholder"
                    >
                      Not set
                    </PlaceholderCell>
                  </TableRow>
                )}
              </TableBody>
            </MachineTable>
          </TableScrollWrapper>
        </CustomAccordionDetails>
      </Accordion>
    </>
  );
}

const CustomAccordionDetails = styled(AccordionDetails)`
  flex-wrap: wrap;
`;

const ButtonWrapper = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  padding: 5px 0 15px;
`;

const TableScrollWrapper = styled("div")`
  width: 100%;
  overflow-x: auto;
`;

const MachineTable = styled(Table)`
  border: none;

  .MuiTableCell-root {
    padding: 8px;
  }
`;

const TableClearCell = styled(TableCell)`
  border-bottom: none;
  color: #616161;
`;

const PlaceholderCell = styled(TableCell)`
  text-align: center;
  color: #616161;
`;

const TableLoadingCell = styled(TableCell)`
  padding: 0;
  height: 5px;
  z-index: 1;
`;

MachineStatusAccordion.propTypes = {
  defaultAccordionExpanded: PropTypes.bool,
  caseId: PropTypes.number,
  machineId: PropTypes.number
};

MachineStatusAccordion.defaultProps = {
  defaultAccordionExpanded: false
};
